/**
 * Form data for the implementation request
 */
interface FormData {
  auditName: string;
  scope: {
    croUx: boolean;
    technicalHealth: boolean;
    accessibility: boolean;
  };
  notes: string;
}

/**
 * Modal component for requesting implementation of recommendations
 */
export class ImplementationRequestModal {
  private modalElement: HTMLElement | null = null;
  private formData: FormData = {
    auditName: '',
    scope: {
      croUx: true,
      technicalHealth: true,
      accessibility: true
    },
    notes: ''
  };

  /**
   * Open the modal
   * @param auditName - The name of the audit
   */
  open(auditName: string): void {
    this.formData.auditName = auditName;
    this.renderModal();
    this.attachHandlers();
    this.show();
  }

  /**
   * Render the modal HTML
   */
  private renderModal(): void {
    const modalHTML = `
      <div id="implementation-modal" class="modal-overlay">
        <div class="modal-content">
          <button class="modal-close" aria-label="Close">&times;</button>

          <h2>Request Implementation</h2>
          <p class="modal-subtitle">
            We'll help get these improvements implemented.
          </p>

          <!-- Step 1: Scope Confirmation -->
          <div class="modal-step">
            <h3>Step 1 - Scope confirmation</h3>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" name="scope" value="cro-ux" checked>
                <span>CRO & UX improvements</span>
              </label>
              <label class="checkbox-label">
                <input type="checkbox" name="scope" value="technical" checked>
                <span>Technical health fixes</span>
              </label>
              <label class="checkbox-label">
                <input type="checkbox" name="scope" value="accessibility" checked>
                <span>Accessibility improvements</span>
              </label>
            </div>
          </div>

          <!-- Step 2: Notes -->
          <div class="modal-step">
            <h3>Step 2 - Notes (optional)</h3>
            <label for="notes" class="input-label">
              Anything you want us to prioritize or avoid?
            </label>
            <textarea
              id="notes"
              name="notes"
              rows="4"
              placeholder="e.g., Focus on mobile experience, avoid changing header design..."
              class="notes-textarea"
            ></textarea>
          </div>

          <!-- Step 3: Submit -->
          <div class="modal-step">
            <h3>Step 3 - Submit</h3>
            <p class="submit-description">
              We'll review the request and follow up with next steps.
            </p>
            <button id="submit-request-btn" class="btn-primary">
              Submit Request
            </button>
          </div>
        </div>
      </div>
    `;

    // Insert modal into DOM
    const existingModal = document.getElementById('implementation-modal');
    if (existingModal) {
      existingModal.remove();
    }
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modalElement = document.getElementById('implementation-modal');
  }

  /**
   * Attach event handlers to modal elements
   */
  private attachHandlers(): void {
    if (!this.modalElement) return;

    // Close button
    const closeBtn = this.modalElement.querySelector('.modal-close');
    closeBtn?.addEventListener('click', () => this.close());

    // Close on overlay click
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) {
        this.close();
      }
    });

    // Track checkbox changes
    const checkboxes = this.modalElement.querySelectorAll('input[name="scope"]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const value = target.value as 'cro-ux' | 'technical' | 'accessibility';

        if (value === 'cro-ux') this.formData.scope.croUx = target.checked;
        if (value === 'technical') this.formData.scope.technicalHealth = target.checked;
        if (value === 'accessibility') this.formData.scope.accessibility = target.checked;
      });
    });

    // Track notes
    const notesTextarea = this.modalElement.querySelector('#notes') as HTMLTextAreaElement;
    notesTextarea?.addEventListener('input', (e) => {
      this.formData.notes = (e.target as HTMLTextAreaElement).value;
    });

    // Submit button
    const submitBtn = this.modalElement.querySelector('#submit-request-btn');
    submitBtn?.addEventListener('click', () => this.submitRequest());
  }

  /**
   * Submit the implementation request
   */
  private submitRequest(): void {
    // Validate at least one scope is selected
    const hasScope = this.formData.scope.croUx ||
                    this.formData.scope.technicalHealth ||
                    this.formData.scope.accessibility;

    if (!hasScope) {
      alert('Please select at least one improvement area.');
      return;
    }

    // Build email body
    const emailBody = this.buildEmailBody();
    const subject = `Implementation Request - ${this.formData.auditName} Audit`;
    // Open mailto
    window.location.href = `mailto:vincent@actionbase.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

    // Close modal
    this.close();
  }

  /**
   * Build the email body text
   */
  private buildEmailBody(): string {
    let body = 'Implementation Request\n\n';
    body += '='.repeat(50) + '\n\n';

    body += `AUDIT: ${this.formData.auditName}\n\n`;

    body += 'REQUESTED SCOPE:\n';
    if (this.formData.scope.croUx) body += '✓ CRO & UX improvements\n';
    if (this.formData.scope.technicalHealth) body += '✓ Technical health fixes\n';
    if (this.formData.scope.accessibility) body += '✓ Accessibility improvements\n';
    body += '\n';

    if (this.formData.notes.trim()) {
      body += 'NOTES:\n';
      body += this.formData.notes + '\n\n';
    }

    body += '='.repeat(50) + '\n';
    body += 'Please review and follow up with next steps.\n';

    return body;
  }

  /**
   * Show the modal
   */
  private show(): void {
    if (this.modalElement) {
      this.modalElement.style.display = 'flex';
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Close the modal
   */
  private close(): void {
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
      // Restore body scroll
      document.body.style.overflow = '';
    }
  }
}
